import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { adminService } from '../../services/adminService'
import AdminLayout from '../../components/admin/AdminLayout'
import { devError } from '../../utils/devLog'
import Button from '../../components/ui/Button'

export default function AdminOrderDetails() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [order, setOrder] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchOrder()
  }, [id])

  const fetchOrder = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await adminService.getOrder(id)
      if (response.success) {
        setOrder(response.order)
      } else {
        setError(response.error || 'Failed to fetch order details')
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to fetch order details. Please try again.'
      setError(errorMessage)
      devError('Error fetching order', err)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount)
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="text-center py-12 text-gray-400">Loading order details...</div>
      </AdminLayout>
    )
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="bg-red-500/20 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mb-4 flex flex-wrap items-center justify-between gap-3">
          <span className="flex-1 min-w-0">{error}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchOrder}>
              Retry
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/admin/orders')}>
              Back to Orders
            </Button>
          </div>
        </div>
      </AdminLayout>
    )
  }

  if (!order) {
    return (
      <AdminLayout>
        <div className="text-center py-12 text-gray-400">Order not found</div>
        <Button onClick={() => navigate('/admin/orders')} variant="outline">
          Back to Orders
        </Button>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="mb-6">
          <Button onClick={() => navigate('/admin/orders')} variant="ghost" size="sm">
            ← Back to Orders
          </Button>
        </div>

        <div className="mb-8">
          <h1 className="text-4xl font-black mb-2">
            <span className="text-white">Order Details</span>
          </h1>
          <p className="text-gray-400">Complete order information and customer details</p>
        </div>

        <div className="space-y-6">
          {/* Order Information */}
          <div className="bg-gradient-to-br from-stadium-black to-gray-900 border-2 border-pitch-green/20 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-white mb-4">Order Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-gray-500 text-sm mb-1">Order ID</p>
                <p className="text-white font-mono text-sm break-all">{order._id}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">Razorpay Order ID</p>
                <p className="text-white font-mono text-sm break-all">{order.razorpayOrderId || 'N/A'}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">Payment Status</p>
                <span
                  className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${
                    order.paymentStatus === 'completed'
                      ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                      : order.paymentStatus === 'failed'
                      ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                      : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                  }`}
                >
                  {order.paymentStatus?.toUpperCase() || 'N/A'}
                </span>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">Plan</p>
                <p className="text-white font-semibold">{order.planName || 'N/A'}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">Amount</p>
                <p className="text-pitch-green text-2xl font-bold">{formatCurrency(order.amount || 0)}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">Created Date</p>
                <p className="text-gray-400 text-sm">{formatDate(order.createdAt)}</p>
              </div>
              <div>
                <p className="text-gray-500 text-sm mb-1">Payment Date</p>
                <p className="text-gray-400 text-sm">{formatDate(order.paymentDate)}</p>
              </div>
            </div>
          </div>

          {/* Customer Information */}
          {order.submissionId && (
            <div className="bg-gradient-to-br from-stadium-black to-gray-900 border-2 border-pitch-green/20 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-white">Customer Information</h2>
                <Button
                  onClick={() => navigate(`/admin/submissions/${order.submissionId?._id ?? order.submissionId}`)}
                  variant="outline"
                  size="sm"
                >
                  View Submission →
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-500 text-sm mb-1">Name</p>
                  <p className="text-white font-semibold">{order.submissionId.name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm mb-1">Email</p>
                  <p className="text-white">{order.submissionId.email || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm mb-1">Phone</p>
                  <p className="text-white">{order.submissionId.phone || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm mb-1">Match Type</p>
                  <p className="text-white">{order.submissionId.matchType || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm mb-1">Focus</p>
                  <p className="text-white">{order.submissionId.focus || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm mb-1">Video Hours</p>
                  <p className="text-white">{order.submissionId.videoHours || 'N/A'}</p>
                </div>
                {order.submissionId.driveLink && (
                  <div className="md:col-span-2">
                    <p className="text-gray-500 text-sm mb-1">Google Drive Link</p>
                    <a
                      href={order.submissionId.driveLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-pitch-green hover:text-neon-green break-all"
                    >
                      {order.submissionId.driveLink}
                    </a>
                  </div>
                )}
                {order.submissionId.additionalNotes && (
                  <div className="md:col-span-2">
                    <p className="text-gray-500 text-sm mb-1">Additional Notes</p>
                    <p className="text-gray-300 bg-gray-800/50 p-3 rounded-lg">
                      {order.submissionId.additionalNotes}
                    </p>
                  </div>
                )}
                {order.submissionId.adminNotes && (
                  <div className="md:col-span-2">
                    <p className="text-gray-500 text-sm mb-1">Admin Notes</p>
                    <p className="text-gray-300 bg-gray-800/50 p-3 rounded-lg">
                      {order.submissionId.adminNotes}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Payment Details */}
          {order.paymentStatus === 'completed' && order.paymentId && (
            <div className="bg-gradient-to-br from-stadium-black to-gray-900 border-2 border-pitch-green/20 rounded-xl p-6">
              <h2 className="text-2xl font-bold text-white mb-4">Payment Details</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-500 text-sm mb-1">Razorpay Payment ID</p>
                  <p className="text-white font-mono text-sm break-all">{order.paymentId}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm mb-1">Payment Method</p>
                  <p className="text-white">{order.paymentMethod || 'N/A'}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </AdminLayout>
  )
}
